import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close,
  FileCopyOutlined,
  History,
  SwapVert,
} from "@mui/icons-material";
import {
  loadCommits,
  loadCases,
  clipClone,
  clipCloneNewCase,
} from "../pages/app/utils/dataLoader";
import { useAuth } from "../store/auth/hooks";
import { useDrawer } from "../store/drawer/hooks";
import { useNavigate } from "react-router-dom";
import { getNavigationUrl } from "../store/navigationUtil";
import { canEditClip } from "../store/AclUtil";
import { CommitHistory } from "./history/CommitHistory";
import { useClip } from "../store/clip/hooks";
import { CloneDialog } from "./dialogs";
import { useModals } from "../store/dialogs/hooks";

export const HistoryDrawer = ({ caseId, clipId, page }) => {
  const previousClipId = useRef(clipId);
  if (clipId !== null) {
    previousClipId.current = clipId;
  }
  const effectiveClipId = previousClipId.current;
  const isEditorPage = page === "editor";
  const isClipsPage = page === "clips";
  const isFilteredFilesPage = page === "filteredFiles";
  const isGlimpseErrorPage = page === "glimpseError";

  const theme = useTheme();
  const navigate = useNavigate();
  const { revertClip } = useClip();
  const { openCloneDialog } = useModals();
  const { showHistoryDrawer, closeHistoryDrawer } = useDrawer();
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [commits, setCommits] = useState([]);
  const [clip, setClip] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [readOnly, setReadOnly] = useState(true);
  const [sortHistory, setSortHistory] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const [disableHistory, setDisableHistory] = useState(false);
  const [iconsDisabled, setIconsDisabled] = useState(true);
  const [clipWorking, setClipWorking] = useState(false);

  const handleClose = () => {
    closeHistoryDrawer();
    setCommits([]);
    setClip(null);
    setLoaded(false);
    setSelectedId(0);
  };

  const handleRevertClip = async (commitId) => {
    handleClose();
    setClipWorking(true);
    const revertedClip = await revertClip(caseId, effectiveClipId, commitId);
    setClipWorking(false);
    if (isClipsPage || isFilteredFilesPage || isGlimpseErrorPage) {
      navigate(getNavigationUrl(caseId, revertedClip));
    } else if (isEditorPage) {
      navigate(0);
    }
  };

  const cloneClip = async (commitId, newCaseId) => {
    closeHistoryDrawer();
    setClipWorking(true);
    await clipClone(caseId, effectiveClipId, commitId, newCaseId);
    setClipWorking(false);
    if (newCaseId) {
      navigate(getNavigationUrl(newCaseId));
    } else {
      navigate(getNavigationUrl(caseId));
    }
  };

  const cloneClipNewCase = async (commitId) => {
    closeHistoryDrawer();
    setClipWorking(true);
    await clipCloneNewCase(caseId, effectiveClipId, commitId);
    setClipWorking(false);
    navigate(getNavigationUrl());
  };

  const load = useCallback(async () => {
    const snapshot = await loadCommits(caseId, effectiveClipId);
    if (!snapshot) {
      navigate(getNavigationUrl(caseId));
    }
    const cases = await loadCases();
    setCases(
      cases
        .filter((c) => c.editable === true && c.id !== Number(caseId))
        .sort((a, b) => (a.updatedEpochMs < b.updatedEpochMs ? 1 : -1)),
    );
    setCommits(snapshot.commits);
    setClip(snapshot.clipSnapshot);
    setLoaded(true);
    setReadOnly(!canEditClip(user, snapshot.clipSnapshot));
  }, [caseId, effectiveClipId, navigate, user]);

  useEffect(() => {
    if (showHistoryDrawer) {
      load();
    }
  }, [load, showHistoryDrawer]);

  useEffect(() => {
    setDisableHistory(selectedId === clip?.commitId);
    setIconsDisabled(selectedId === 0);
  }, [selectedId, clip]);

  useEffect(() => {
    if (sortHistory) {
      setCommits((prev) =>
        [...prev].sort((a, b) =>
          a.createdEpochMs < b.createdEpochMs ? 1 : -1,
        ),
      );
    } else {
      setCommits((prev) =>
        [...prev].sort((a, b) =>
          a.createdEpochMs > b.createdEpochMs ? 1 : -1,
        ),
      );
    }
  }, [sortHistory]);

  return (
    <Box sx={{ p: 3 }}>
      <Drawer
        anchor="right"
        aria-hidden={!showHistoryDrawer}
        open={showHistoryDrawer}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 1,
            height: "calc(100% - 160px)",
            top: 76,
            width: 900,
          },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={50}
          p={1.5}
          sx={{
            backgroundColor: theme.palette.primary.dark,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography color="common.white" variant="h5">
              History:
            </Typography>
            <Typography
              color="common.white"
              variant="h6"
              sx={{
                fontWeight: 400,
                maxWidth: 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {clip?.description}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: "common.white" }}
          >
            <Close />
          </IconButton>
        </Box>

        {!loaded ? (
          <Box
            display="flex"
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            p={3}
          >
            <CircularProgress />
          </Box>
        ) : (
          <CommitHistory
            commits={commits}
            onSelectedId={(id) => setSelectedId(id)}
          />
        )}
        <Box
          display="flex"
          justifyContent="end"
          alignItems="center"
          height={50}
          sx={{
            py: 2,
            px: 1,
            backgroundColor: theme.palette.primary.dark,
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          }}
        >
          {clipWorking ? (
            <Box display="flex">
              <CircularProgress color="info" />
            </Box>
          ) : (
            <>
              <Tooltip
                placement="top"
                title={`Sort commits by ${sortHistory ? "oldest" : "newest"}`}
              >
                <span>
                  <IconButton
                    disabled={commits.length <= 1}
                    sx={{
                      color: theme.palette.common.white,
                      transition: "all 250ms ease-in-out",
                      "&.Mui-disabled": {
                        "& .MuiSvgIcon-root": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      },
                    }}
                    size="large"
                    aria-label="Sort"
                    onClick={() => setSortHistory(!sortHistory)}
                  >
                    <SwapVert />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement="top" title="Revert to Commit">
                <span>
                  <IconButton
                    sx={{
                      color: theme.palette.common.white,
                      transition: "all 250ms ease-in-out",
                      "&.Mui-disabled": {
                        "& .MuiSvgIcon-root": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      },
                    }}
                    size="large"
                    aria-label="Revert"
                    onClick={() => handleRevertClip(selectedId)}
                    disabled={readOnly || iconsDisabled || disableHistory}
                  >
                    <History />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip placement="top" title="Clone Clip">
                <span>
                  <IconButton
                    sx={{
                      color: theme.palette.common.white,
                      transition: "all 250ms ease-in-out",
                      "&.Mui-disabled": {
                        "& .MuiSvgIcon-root": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      },
                    }}
                    size="large"
                    aria-label="Clone"
                    onClick={() => openCloneDialog()}
                    disabled={iconsDisabled}
                  >
                    <FileCopyOutlined />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Drawer>
      <CloneDialog
        cases={cases}
        cloneClip={cloneClip}
        cloneClipNewCase={cloneClipNewCase}
        readOnly={readOnly}
        selectedId={selectedId}
      />
    </Box>
  );
};
